import { useDebounceFn } from 'ahooks';
import { Card, Form, FormInstance, Space } from 'antd';
import { debounce, get, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getParamFromUrl, replaceHistory, toSearchObj } from '@/infrastructure/history';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick, useReportPage } from '@/infrastructure/reporter/datong/report';
import {
  CommonDateRange,
  DATE_TIME_FORMAT,
  formatExactTimeRange,
  formatTimeRange,
  getAndFormatDateRangeByShortCut,
  getDateRangeByShortCut,
  getDefaultTimeRange,
} from '@/infrastructure/utils/timestamp';
import { reportEditSearchCriteria } from '@/presentation/components/exception-form/utils';
import {
  BUGLY_DATE_RANGE_BY_SHORT_CUT,
  CUSTOM,
  shortRanges,
} from '@/presentation/components/form/components/time-range-cascade';
import { CompareStatusFormType, compareStatusInitialValues } from '@/presentation/components/issue-table/compare-form';

import { TagInfo } from '@/presentation/components/question-tag/api';
import { PropsWithStyles } from '@/proto/common';
import { IssueType } from '@/proto/issue';
import { TimeType } from '@/proto/metric';
import { FieldList, getCompareToken, getToken, parseCompareToken } from '@/proto/token';
import { ITagClick } from '@/proto/type';

export interface IssueFormProps {
  onFinish: (value: Record<string, any>) => void;
  initialValue: FieldList & { timeRange: string[] };
  tokenInitValues: FieldList;
  form: FormInstance
}

export interface IssueListProps {
  token: string;
  fields?: FieldList;
  fetch: boolean;
  formChange: (value: Record<string, any>, fields: Record<string, any>) => void;
  headToken: string;
  form: FormInstance;
  compareFilterForm: FormInstance;
  compareStatusChange: (allValues: CompareStatusFormType) => void;
  tagClick: ITagClick;
  tokenInitValues: FieldList;
}

export interface IssueTrendAnalysisProps {
  token: string;
  fields?: FieldList;
  headToken: string;
  fetch: boolean;
  pageFetch: boolean;
  queryCompareToken: (fields: Record<string, any>) => Promise<string>;
}

interface IProps {
  issueType?: IssueType;
  pgId: string;
  defaultFields?: FieldList;
  getDefaultFields?: () => Promise<FieldList>;
  renderForm: (props: IssueFormProps) => React.ReactElement;
  renderList: (props: IssueListProps) => React.ReactElement;
  renderTrendAnalysis?: (props: IssueTrendAnalysisProps) => React.ReactElement | null;
  onInitFields?: (fields: FieldList, judgmentTimeCallback: () => void) => void;
  customHeadTokenFilterName?: string[];// 上半部分 token过滤字段
}

export type TimeRangeType = { startTime: string, endTime: string } | null;

const dateFormat = 'YYYY-MM-DD HH:mm:ss';

// 头部表单 token需要过滤的字段
const DEFAULT_HEAD_TOKEN_FILTER_NAME = [
  'tapdHandler',
  'tagColor',
  'tapdStatus',
  'issueStatus',
  'tags',
  'topNum',
  'tapdHandlerOperatorConvert',
  'tagColorOperatorConvert',
  'tapdStatusOperatorConvert',
  'issueStatusOperatorConvert',
  'tagsOperatorConvert',
  'costTimeAvg',
  'costTimeAvgCompare',
];

export const Issues: FC<PropsWithStyles<IProps>> = (props) => {
  const { pgId, customHeadTokenFilterName, defaultFields, getDefaultFields, renderForm,
    renderList, className, renderTrendAnalysis, onInitFields } = props;
  useReportPage(pgId);
  const [token, setToken] = useState('');
  const [compareFilterForm] = Form.useForm();
  const [fields, setFields] = useState<FieldList>({
    ...getDefaultTimeRange(),
  });
  const [tokenInitValues, setTokenInitValues] = React.useState<FieldList>({});
  const [tableFields, setTableFields] = useState<FieldList>({});
  const [headToken, setHeadToken] = useState('');
  const [customDate, setCustomDate] = useState<string>(getParamFromUrl('customDate'));
  const [pageFetch, setPageFetch] = useState(true);
  const [fetch, setFetch] = useState(true);
  const [form] = Form.useForm();
  const [tableForm] = Form.useForm();
  const { pathname } = useLocation();
  const history = useHistory();

  const getHeadToken = async (newFields: Record<string, any>) => {
    const hederFields: any = {};
    Object.keys(newFields).forEach((key) => {
      const filterNames = DEFAULT_HEAD_TOKEN_FILTER_NAME.concat(customHeadTokenFilterName || []);

      const compareFilterNames = filterNames.map(name => `${name}Compare`);
      if (!filterNames.concat(compareFilterNames).includes(key) && key !== 'filters') {
        hederFields[key] = get(newFields, key);
      }
    });
    return newFields.isCompareOpen
      ? await getCompareToken(hederFields)
      : await getToken(hederFields);
  };

  const updateToken = async (fields: FieldList, head = true) => {
    if (!fields) {
      return;
    }
    const newFields = {
      ...fields,
      timeType: fields.timeType || 'report',
    };

    const token = fields.isCompareOpen
      ? await getCompareToken(newFields)
      : await getToken(newFields);
    setToken(token);
    setFetch(fetch => !fetch);
    if (head) {
      const headToken = await getHeadToken(newFields);
      setHeadToken(headToken);
      setPageFetch(fetch => !fetch);
    }
  };

  const onSearch = async (values: Record<string, any>) => {
    const { accurateDate, timeRange, customDate, isCompareOpen, anrIssueId, ...otherFiels } = values;
    let ranges: TimeRangeType = {
      startTime: '',
      endTime: '',
    };
    if (timeRange) {
      ranges = formatExactTimeRange(timeRange as string[]);
    } else if (accurateDate) {
      const newTimeRange = [accurateDate, accurateDate];
      ranges = formatTimeRange(newTimeRange);
    }
    let { timeRangeCompare } = values;
    const isDateRangeByShortCut = BUGLY_DATE_RANGE_BY_SHORT_CUT.includes(values?.timeQuick ?? '');
    const isDateRangeCompareByShortCut = BUGLY_DATE_RANGE_BY_SHORT_CUT.includes(values?.timeQuickCompare ?? '');
    if (customDate) {
      ranges = getAndFormatDateRangeByShortCut(customDate);
    }
    if (isDateRangeByShortCut) {
      ranges = getAndFormatDateRangeByShortCut(values?.timeQuick, shortRanges);
    }
    if (isDateRangeCompareByShortCut) {
      const dateRange = getDateRangeByShortCut(values?.timeQuickCompare, shortRanges);
      timeRangeCompare = [
        moment(dateRange[0]).format(dateFormat),
        moment(dateRange[1]).format(dateFormat),
      ];
    }
    setCustomDate(customDate);
    const tableFormData = tableForm.getFieldsValue();

    const compareTableFormData: { [key: string]: string }  = {};
    Object.keys(tableFormData).forEach(key => compareTableFormData[`${key}Compare`] = tableFormData[key]);

    const compareFilterValue = compareFilterForm?.getFieldsValue?.(true);
    const newCompareFilterValue = isEmpty(compareFilterValue) ? compareStatusInitialValues : compareFilterValue;
    const { tapdHandler, tagColor, ...res } = tableFormData;
    const newFields = {
      ...compareTableFormData,
      ...ranges,
      ...otherFiels,
      ...tableFormData,
      anrIssueId,
      isCompareOpen,
      timeRangeCompare,
      ...(isCompareOpen ? newCompareFilterValue : undefined),
      newTagListOperator:
        tableFormData.tagsOperatorConvert
          && tableFormData.tags?.length !== 0
          && !tableFormData.tags?.some((val: string) => val === '')
          ? 'NOT'
          : '',
      tapdHandlerOperator:
        tableFormData.tapdHandlerOperatorConvert
          && tableFormData.tapdHandler?.length !== 0
          && !tableFormData.tapdHandler?.some((val: string) => val === '')
          ? 'NOT'
          : '',
      issueStatusOperator:
        tableFormData.issueStatusOperatorConvert
          && tableFormData.issueStatus?.length !== 0
          && !tableFormData.issueStatus?.some((val: string) => val === '')
          ? 'NOT'
          : '',
      tapdStatusOperator:
        tableFormData.tapdStatusOperatorConvert
          && tableFormData.tapdStatus?.length !== 0
          && tableFormData.tapdStatus?.some((val: string) => val === '')
          ? 'NOT'
          : '',
      tagColorOperator:
        tableFormData.tagColorOperatorConvert
          && tableFormData.tagColor?.length !== 0
          && !tableFormData.tagColor?.some((val: string) => val === '')
          ? 'NOT'
          : '',
    };

    setFields(newFields);
    setTableFields({
      tapdHandler,
      tagColor,
      ...res,
      isCompareOpen,
      timeWindow: otherFiels.timeWindow,
      ...(isCompareOpen ? newCompareFilterValue : undefined) });
    await updateToken(newFields);
    reportEditSearchCriteria(values, pgId);
    reportClick(pgId, CUSTOM_ACTIONS.commonDoSearch, {
      from: pgId,
    });
  };

  const parseFields = (fields: FieldList) => {
    const { startTime, endTime, ...values } = fields;

    const timeRange = [
      moment.unix(startTime).toString(),
      moment.unix(endTime).toString(),
    ];
    const accurateDate = moment.unix(startTime).toString();
    return {
      timeRange,
      accurateDate,
      ...values,
    };
  };

  useEffect(() => {
    const load = async () => {
      const token = getParamFromUrl('token');
      if (token) {
        const fields = await parseCompareToken(token);
        const isDateRangeByShortCut = BUGLY_DATE_RANGE_BY_SHORT_CUT.includes(fields?.timeQuick ?? '');
        const isDateRangeCompareByShortCut = BUGLY_DATE_RANGE_BY_SHORT_CUT.includes(fields?.timeQuickCompare ?? '');
        const dateRange = getDateRangeByShortCut(fields?.timeQuick, shortRanges);
        const dateRangeCompare = getDateRangeByShortCut(fields?.timeQuickCompare, shortRanges);
        // 判断是否为当天，否则显示自定义
        const isToday = fields.endTime === dateRange?.[1]?.unix?.();
        const isTodayCompare = fields?.isCompareOpen
          && moment(fields?.timeRangeCompare?.[1], DATE_TIME_FORMAT).unix()
          === dateRangeCompare?.[1]?.unix?.();
        if (!isToday && isDateRangeByShortCut) {
          fields.timeQuick = 'custom';
        }
        if (isDateRangeCompareByShortCut && !isTodayCompare) {
          fields.timeQuickCompare = 'custom';
        }
        const timeRange = isDateRangeByShortCut && isToday
          ? getAndFormatDateRangeByShortCut(fields?.timeQuick, shortRanges)
          : getAndFormatDateRangeByShortCut(customDate);
        const timeRangeCompare = isDateRangeCompareByShortCut
          ? [
            moment(dateRangeCompare[0]).format(dateFormat),
            moment(dateRangeCompare[1]).format(dateFormat),
          ]
          : fields.timeRangeCompare;
        const defaultValue = defaultFields || (getDefaultFields ? await getDefaultFields() : undefined) || {
          ...getDefaultTimeRange(),
        };
        const newFields = {
          ...(isEmpty(fields) || !fields ? defaultValue : fields),
          tags: fields.tagList,
          tagColor: fields?.tagColor?.split?.(','),
          ...timeRange,
          timeRangeCompare,
          newTagListOperator: fields.newTagListOperator,
          tapdHandlerOperator: fields.tapdHandlerOperator,
          issueStatusOperator: fields?.issueStatusOperator,
          tapdStatusOperator: fields?.tapdStatusOperator,
          tagColorOperator: fields?.tagColorOperator,
          tagsOperatorConvert: fields?.newTagListOperator === 'NOT',
          tapdHandlerOperatorConvert: fields?.tapdHandlerOperator === 'NOT',
          issueStatusOperatorConvert: fields?.issueStatusOperator === 'NOT',
          tapdStatusOperatorConvert: fields?.tapdStatusOperator === 'NOT',
          tagColorOperatorConvert: fields?.tagColorOperator === 'NOT',
        };

        setFields(newFields);
        setTokenInitValues(newFields || {});
        onInitFields?.(parseFields(newFields), judgmentTimeCallback);
        setTableFields(newFields);
        if (isDateRangeByShortCut) {
          await updateToken(newFields);
        } else {
          setToken(token);
          setFetch(fetch => !fetch);
          const headToken = await getHeadToken(newFields);
          setHeadToken(headToken);
          setPageFetch(fetch => !fetch);
        }
        if (fields?.isCompareOpen) {
          compareFilterForm.setFieldsValue({
            compareStatus: fields.compareStatus,
            topData: fields.topData,
          });
        }
      } else {
        const fields = defaultFields || (getDefaultFields ? await getDefaultFields() : undefined) || {
          ...getDefaultTimeRange(),
        };

        setFields(fields);
        setTableFields(fields);
        onInitFields?.(parseFields(fields), judgmentTimeCallback);
        await updateToken(fields);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const currentSearch = toSearchObj(window.location.search);
    const searchObj = {
      ...currentSearch,
      token,
      customDate: customDate || '',
    };
    replaceHistory(history, pathname, searchObj);
  }, [token]);

  const formChange = async (_: Record<string, any>, params: Record<string, any>) => {
    const { isCompareOpen, compareStatus, topData } = fields;

    // 清除搜索时默认值
    const formValue = form.getFieldsValue();
    const { timeRange, customDate, ...otherFiels } = formValue;
    // topNum 校验拦截
    const tableFormValue = tableForm.getFieldsValue();
    const topNum = parseInt(tableFormValue?.topNum, 10) || 0;
    if (topNum && topNum > 3000) {
      return;
    }

    let ranges: TimeRangeType = formatExactTimeRange(timeRange as string[]);
    let { timeRangeCompare } = formValue;
    const isDateRangeByShortCut = BUGLY_DATE_RANGE_BY_SHORT_CUT.includes(formValue?.timeQuick ?? '');
    const isDateRangeCompareByShortCut = BUGLY_DATE_RANGE_BY_SHORT_CUT.includes(formValue?.timeQuickCompare ?? '');
    if (customDate) {
      ranges = getAndFormatDateRangeByShortCut(customDate);
    }
    if (isDateRangeByShortCut) {
      ranges = getAndFormatDateRangeByShortCut(formValue?.timeQuick, shortRanges);
    }
    if (isDateRangeCompareByShortCut) {
      const dateRange = getDateRangeByShortCut(formValue?.timeQuickCompare, shortRanges);
      timeRangeCompare = [
        moment(dateRange[0]).format(dateFormat),
        moment(dateRange[1]).format(dateFormat),
      ];
    }

    setCustomDate(customDate);
    const {
      tapdHandler = [''],
      tagColor = [''],
      tapdStatus = [''],
      issueStatus = [''],
      tags = [''],
      tapdHandlerOperatorConvert = false,
      tagColorOperatorConvert = false,
      tapdStatusOperatorConvert = false,
      issueStatusOperatorConvert = false,
      tagsOperatorConvert = false,
      costTimeAvg,
      costTimeAvgCompare,
      ...res
    } = params;
    const values = {
      ...ranges,
      ...otherFiels,
      // 不要默认空字符串了，空就不传，不然后台字段格式校验会报错
      costTimeAvgMin: costTimeAvg?.value?.[0],
      costTimeAvgMax: costTimeAvg?.value?.[1],
      costTimeAvgMinCompare: costTimeAvgCompare?.value?.[0],
      costTimeAvgMaxCompare: costTimeAvgCompare?.value?.[1],
      timeRangeCompare,
      tapdHandler,
      tagColor,
      tapdStatus,
      issueStatus,
      tags,
      tapdHandlerOperator: tapdHandlerOperatorConvert && tapdHandler.length !== 0 && !tapdHandler.some((val: string) => val === '') ? 'NOT' : '',
      tagColorOperator: tagColorOperatorConvert && tagColor.length !== 0 && !tagColor.some((val: string) => val === '')  ? 'NOT' : '',
      tapdStatusOperator: tapdStatusOperatorConvert && tapdStatus.length !== 0 && !tapdStatus.some((val: string) => val === '')  ? 'NOT' : '',
      issueStatusOperator: issueStatusOperatorConvert && issueStatus.length !== 0 && !issueStatus.some((val: string) => val === '') ? 'NOT' : '',
      newTagListOperator: tagsOperatorConvert && tags.length !== 0 && !tags.some((val: string) => val === '') ? 'NOT' : '',
      ...res,
      isCompareOpen,
      compareStatus,
      topData,
    };
    setTableFields({
      tapdHandler,
      tagColor,
      tapdStatus,
      issueStatus,
      tags,
      tapdHandlerOperator: tapdHandlerOperatorConvert && tapdHandler.length !== 0 && !tapdHandler.some((val: string) => val === '') ? 'NOT' : '',
      tagColorOperator: tagColorOperatorConvert && tagColor.length !== 0 && !tagColor.some((val: string) => val === '')  ? 'NOT' : '',
      tapdStatusOperator: tapdStatusOperatorConvert && tapdStatus.length !== 0 && !tapdStatus.some((val: string) => val === '')  ? 'NOT' : '',
      issueStatusOperator: issueStatusOperatorConvert && issueStatus.length !== 0 && !issueStatus.some((val: string) => val === '') ? 'NOT' : '',
      newTagListOperator: tagsOperatorConvert && tags.length !== 0 && !tags.some((val: string) => val === '') ? 'NOT' : '',
      ...res,
      isCompareOpen,
      compareStatus,
      topData,
      costTimeAvg,
      // @ts-ignore
      costTimeAvgCompare,
      costTimeAvgMinCompare: costTimeAvgCompare?.value?.[0],
      costTimeAvgMaxCompare: costTimeAvgCompare?.value?.[1],
      // 不要默认空字符串了，空就不传，不然后台字段格式校验会报错
      costTimeAvgMin: costTimeAvg?.value?.[0],
      costTimeAvgMax: costTimeAvg?.value?.[1],
    });
    await doSearch(values);
  };

  const { run: doSearch } = useDebounceFn(async (values: any) => {
    await updateToken(values, false);
    reportEditSearchCriteria(values, pgId);
    reportClick(pgId, CUSTOM_ACTIONS.commonDoSearch, {
      from: pgId,
    });
  }, { wait: 800 });

  const compareStatusChange = debounce(async (allValues: CompareStatusFormType) => {
    setFields((fields: any) => ({ ...fields, ...allValues }));
    setTableFields((fields: any) => ({ ...fields, ...allValues }));
    const newValues = { ...fields, ...tableFields, ...allValues };
    await updateToken(newValues, false);
    reportEditSearchCriteria(newValues, pgId);
    reportClick(pgId, CUSTOM_ACTIONS.commonDoSearch, {
      from: pgId,
    });
  }, 300);

  // 二次调用，用于趋势分析获取对比token，需过滤不属于趋势分析的筛选字段
  const queryCompareToken = async (fields: Record<string, any>) => {
    const newFields: any = {};
    // 过滤字段
    Object.keys(fields).forEach((key) => {
      const filterNames = DEFAULT_HEAD_TOKEN_FILTER_NAME.concat(customHeadTokenFilterName || []);

      const compareFilterNames = filterNames.map(name => `${name}Compare`);
      if (!filterNames.concat(compareFilterNames).includes(key) && key !== 'filters') {
        newFields[key] = get(fields, key);
      }
    });

    let startTime = '';
    let endTime = '';
    if (newFields.customDateCompare) {
      const dateRange = getDateRangeByShortCut(newFields.customDateCompare);
      startTime = dateRange[0].unix().toString();
      endTime = dateRange[1].unix().toString();
    } else {
      startTime = moment(newFields.timeRangeCompare?.[0])
        .unix()
        .toString();
      endTime = moment(newFields.timeRangeCompare?.[1])
        .unix()
        .toString();
    }
    return await getToken({ ...newFields, startTime, endTime });
  };

  const judgmentTimeCallback = () => {
    form.setFieldsValue({
      timeQuick: CUSTOM,
      timeWindow: TimeType.DAY,
      timeRange: CommonDateRange.lastDaysFull(90),
    });
    form.submit();
  };

  const tagClick = (tag?: TagInfo) => {
    formChange({}, { ...tableFields, tags: [tag?.tagName] });
  };

  return (
    <Space
      className={className}
      direction={'vertical'}
      size={'middle'}
      style={{ width: '100%' }}
    >
      <Card bordered={false}>
        {renderForm({
          onFinish: onSearch,
          initialValue: parseFields(fields),
          form,
          tokenInitValues,
        })}
      </Card>
      {renderTrendAnalysis?.({ token, fields, headToken, fetch, queryCompareToken, pageFetch })}
      <Card bordered={false}>
        {renderList({
          token,
          fields: tableFields,
          formChange,
          headToken,
          fetch,
          form: tableForm,
          compareFilterForm,
          compareStatusChange,
          tagClick,
          tokenInitValues,
        })}
      </Card>
    </Space>
  );
};
